import { useState, type FormEvent } from "react";
import { knowledgeCollectionViewCopy } from "../app/i18n";
import { knowledgeArticleStatusBadgeClass, knowledgeArticleStatusLabel } from "../app/articleStatus";
import type { AppLanguage } from "../app/language";
import { AnimatedModal } from "../app/ui";
import type { KnowledgeArticle, KnowledgeCollection } from "../app/types";

type KnowledgeCollectionViewProps = {
  collection: KnowledgeCollection;
  articles: KnowledgeArticle[];
  language: AppLanguage;
  canCreateArticle?: boolean;
  canManageCollection?: boolean;
  onBack: () => void;
  onCreateArticle: () => void;
  onOpenArticle: (articleId: string) => void;
  onDeleteCollection: () => Promise<void>;
};

export function KnowledgeCollectionView({ collection, articles, language, canCreateArticle = true, canManageCollection = true, onBack, onCreateArticle, onOpenArticle, onDeleteCollection }: KnowledgeCollectionViewProps) {
  const copy = knowledgeCollectionViewCopy[language];
  const featuredArticles = articles.filter(article => article.isFeatured).slice(0, 3);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [hasConfirmedArticleDeletion, setHasConfirmedArticleDeletion] = useState(false);
  const [isDeletingCollection, setIsDeletingCollection] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteConfirmationText("");
    setHasConfirmedArticleDeletion(false);
    setDeleteError(null);
  };

  const canDeleteCollection = hasConfirmedArticleDeletion && deleteConfirmationText.trim().toLowerCase() === "delete";

  const handleDeleteCollection = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!hasConfirmedArticleDeletion) {
      setDeleteError(copy.errors.confirmArticles);
      return;
    }

    if (deleteConfirmationText.trim().toLowerCase() !== "delete") {
      setDeleteError(copy.errors.typeDelete);
      return;
    }

    setIsDeletingCollection(true);
    setDeleteError(null);

    try {
      await onDeleteCollection();
      closeDeleteDialog();
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : copy.errors.deleteFailed);
    } finally {
      setIsDeletingCollection(false);
    }
  };

  return (
    <>
      <section className="space-y-8 p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="w-full">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={onBack}
              className="rounded-full bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)] transition-colors hover:bg-[#f9fafb]"
            >
              ← {copy.page.back}
            </button>

            <div className="flex flex-wrap items-center justify-end gap-3">
              {canCreateArticle ? (
                <button
                  type="button"
                  onClick={onCreateArticle}
                  className="brand-primary-button inline-flex h-11 items-center rounded-xl px-5 text-sm font-bold"
                >
                  {copy.page.newArticle}
                </button>
              ) : null}
              {canManageCollection ? (
                <button
                  type="button"
                  onClick={() => {
                    setDeleteError(null);
                    setIsDeleteDialogOpen(true);
                  }}
                  className="inline-flex h-11 items-center rounded-xl border border-[#fda29b] bg-white px-5 text-sm font-bold text-[#b42318] transition-colors hover:bg-[#fef3f2]"
                >
                  {copy.page.deleteCollection}
                </button>
              ) : null}
            </div>
          </div>
          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{collection.name}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">{collection.detail}</p>
        </div>

      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
        <div className="space-y-6">
          {featuredArticles.length > 0 ? (
            <section className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.featured.eyebrow}</p>
                  <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.featured.title}</h2>
                </div>
                <span className="rounded-full bg-[#ecfdf3] px-4 py-2 text-xs font-semibold text-[#027a48]">{copy.featured.count(featuredArticles.length)}</span>
              </div>

              <div className="mt-6 grid gap-4 lg:grid-cols-3">
                {featuredArticles.map(article => (
                  <button key={article.id} type="button" onClick={() => onOpenArticle(article.id)} className="rounded-[24px] bg-[#f9f9fa] p-5 text-left transition-all hover:bg-[#f3f4f6]">
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{article.meta}</p>
                    <h3 className="mt-3 font-headline text-xl font-extrabold tracking-[-0.03em] text-[#101828]">{article.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[#667085]">{article.summary || copy.featured.noSummary}</p>
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <section className="rounded-[32px] border border-white/60 bg-white p-8 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.articles.eyebrow}</p>
                <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.articles.title(collection.name)}</h2>
              </div>
              <span className="rounded-full bg-[#f2f4f7] px-4 py-2 text-xs font-semibold text-[#475467]">{copy.articles.total(articles.length)}</span>
            </div>

            <div className="mt-6 space-y-3">
              {articles.length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.articles.emptyTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.articles.emptyDetail}</p>
                </div>
              ) : (
                articles.map(article => (
                  <button key={article.id} type="button" onClick={() => onOpenArticle(article.id)} className="w-full rounded-[24px] border border-[#edf0f2] bg-[#fcfcfd] px-5 py-4 text-left transition-all hover:border-[#cfd4dc] hover:bg-white">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{article.meta}</p>
                        <h3 className="mt-2 text-lg font-extrabold tracking-[-0.03em] text-[#101828]">{article.title}</h3>
                        <p className="mt-2 text-sm leading-6 text-[#667085]">{article.summary || copy.articles.noSummary}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${knowledgeArticleStatusBadgeClass(article.status)}`}>{knowledgeArticleStatusLabel(article.status, language)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.summary.eyebrow}</p>
            <div className="mt-5 space-y-3">
              <div className="rounded-2xl bg-[#f9f9fa] px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.summary.featured}</p>
                <p className="mt-2 text-2xl font-extrabold tracking-[-0.03em] text-[#101828]">{featuredArticles.length}</p>
              </div>
              <div className="rounded-2xl bg-[#f9f9fa] px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.summary.popular}</p>
                <p className="mt-2 text-2xl font-extrabold tracking-[-0.03em] text-[#101828]">{articles.filter(article => article.isPopular).length}</p>
              </div>
              <div className="rounded-2xl bg-[#f9f9fa] px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">{copy.summary.drafts}</p>
                <p className="mt-2 text-2xl font-extrabold tracking-[-0.03em] text-[#101828]">{articles.filter(article => article.status === "Draft").length}</p>
              </div>
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-[linear-gradient(180deg,#0b1f18_0%,#123528_100%)] p-6 text-white shadow-[0_20px_40px_rgba(26,28,29,0.14)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/60">{copy.guidance.eyebrow}</p>
            <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em]">{copy.guidance.title}</h2>
            <p className="mt-3 text-sm leading-6 text-white/70">
              {copy.guidance.description}
            </p>
          </section>
        </aside>
        </div>
      </section>

      <AnimatedModal
        open={isDeleteDialogOpen && canManageCollection}
        onClose={closeDeleteDialog}
        backdropAriaLabel={copy.delete.closeDialog}
        overlayClassName="fixed inset-0 z-[80] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 w-full max-w-2xl rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#f04438]">{copy.delete.eyebrow}</p>
            <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{copy.delete.title(collection.name)}</h2>
            <p className="mt-2 text-sm text-[#667085]">
              {copy.delete.description(articles.length)}
            </p>
          </div>
          <button
            type="button"
            onClick={closeDeleteDialog}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleDeleteCollection} className="mt-6 space-y-5">
          <label className="flex items-start gap-3 rounded-2xl border border-[#fecaca] bg-[#fef2f2] px-4 py-4 text-sm text-[#7a271a]">
            <input
              type="checkbox"
              checked={hasConfirmedArticleDeletion}
              onChange={event => setHasConfirmedArticleDeletion(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-[#fda29b] text-[#b42318] focus:ring-[#b42318]"
            />
            <span>{copy.delete.confirmCheckbox}</span>
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.delete.typeDelete}</span>
            <input
              type="text"
              value={deleteConfirmationText}
              onChange={event => setDeleteConfirmationText(event.target.value)}
              placeholder={copy.delete.placeholder}
              className="mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[#f04438] focus:ring-2 focus:ring-[#f04438]/10"
            />
          </label>

          {deleteError ? <p className="text-sm font-semibold text-[#b42318]">{deleteError}</p> : null}

          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={closeDeleteDialog} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]">
              {copy.delete.cancel}
            </button>
            <button
              type="submit"
              disabled={!canDeleteCollection || isDeletingCollection}
              className="rounded-xl bg-[#b42318] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#912018] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isDeletingCollection ? copy.delete.deleting : copy.delete.deleteCollection}
            </button>
          </div>
        </form>
      </AnimatedModal>
    </>
  );
}
